// src/services/authService.ts

import { getCookie, setCookie, deleteCookie } from "../utils/cookies";
import { decodeJwt } from "../utils/jwt";
import { clearPendingInnerCircleClaim } from "../utils/innerCircleClaimContinuation";
import {
	fetchCurrentUser,
	loginUser,
	loginWithCode,
	createUserAccount,
	requestPasswordReset,
	submitNewPassword,
} from "../api/auth";
import { setNavCredits } from "../components/core/navigation"; // Assuming renderNav updates based on state
import { messageToast } from "../components/common/toastMessage";
import { LoginResponse, User, JwtPayload } from "../types/api";
import {
	DEFAULT_COOKIE_EXPIRY_DAYS,
	TEMP_USER_COOKIE_EXPIRY_HOURS,
	TEMP_USER_DEFAULT_EXPIRY_HOURS,
} from "../utils/config";

// PostHog is initialized via the inline snippet in the HTML template and
// attaches itself to `window.posthog`. Type lives in src/types/globals.d.ts.

/**
 * Retrieves the current authentication token from cookies or session storage.
 * Prefers cookie, falls back to session storage.
 * @returns The auth token string or null if not found.
 */
export function getAuthToken(): string | null {
	return getCookie("token") || sessionStorage.getItem("token");
}

/**
 * Sets the authentication token in both cookie and session storage.
 * @param token - The authentication token string.
 * @param expiryDays - Cookie expiry in days (defaults to DEFAULT_COOKIE_EXPIRY_DAYS).
 */
function setAuthToken(
	token: string,
	expiryDays: number = DEFAULT_COOKIE_EXPIRY_DAYS,
): void {
	setCookie("token", token, expiryDays, "days");
	sessionStorage.setItem("token", token);
}

/**
 * Public wrapper around the internal setAuthToken so flows that receive a JWT
 * in a response body (e.g., voucher claim) can persist it from JS instead of
 * relying solely on the response's Set-Cookie header. This avoids an iOS
 * Safari race where a synchronous navigation after `await fetch(...)` can
 * begin before Set-Cookie headers are committed to the cookie jar.
 */
export function persistAuthToken(
	token: string,
	options?: { isTemp?: boolean; expiryDays?: number },
): void {
	setAuthToken(token, options?.expiryDays ?? DEFAULT_COOKIE_EXPIRY_DAYS);
	if (options?.isTemp !== undefined) {
		const value = options.isTemp ? "true" : "false";
		sessionStorage.setItem("is_temporary", value);
		// Match the backend voucher-claim cookie expiry (24h). Using the shorter
		// TEMP_USER_COOKIE_EXPIRY_HOURS here would clobber the backend's longer
		// expiry and cause temp users to stop being recognised as temp after 1h,
		// since many UI flows key off getCookie("is_temporary").
		setCookie("is_temporary", value, TEMP_USER_DEFAULT_EXPIRY_HOURS, "hours");
	}
}

/**
 * Clears the authentication token from cookies and session storage.
 */
export function clearAuthToken(): void {
	deleteCookie("token");
	sessionStorage.removeItem("token");
	// Clear related user info from session storage
	sessionStorage.removeItem("sci");
	sessionStorage.removeItem("credits");
	sessionStorage.removeItem("is_temporary");
	sessionStorage.removeItem("user_email");
	deleteCookie("is_temporary");
	// Optionally reset PostHog identification
	window.posthog?.reset?.();
}

// --- Temporary-user session stashing ---------------------------------------
// When a logged-in temporary user lands on a hole without a voucher code we
// want to terminate their session (so they cannot keep paying / recording as
// the temp user) but still preserve a link to their temp account so any
// recordings they create after upgrading get reassigned via the existing
// backend merge hooks (these all read the JWT `token` cookie).
//
// Approach: move the JWT from the cookie into localStorage under a stash key
// with a 24h TTL, clear the live auth state, then restore it as the `token`
// cookie *just before* the next auth action (login / signup / social click)
// so the backend sees a valid temp JWT and triggers `reassignRecordings`.

const TEMP_STASH_KEY = "pending_temp_token";
const TEMP_STASH_TTL_MS = 24 * 60 * 60 * 1000; // 24h, matches backend temp token expiry

interface TempStash {
	token: string;
	expiresAt: number;
}

function readTempStash(): TempStash | null {
	try {
		const raw = localStorage.getItem(TEMP_STASH_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as TempStash;
		if (!parsed?.token || !parsed?.expiresAt || Date.now() > parsed.expiresAt) {
			localStorage.removeItem(TEMP_STASH_KEY);
			return null;
		}
		return parsed;
	} catch {
		localStorage.removeItem(TEMP_STASH_KEY);
		return null;
	}
}

/**
 * Returns the stashed temp user email (JWT `sub`), or null if no valid stash.
 */
export function getStashedTempEmail(): string | null {
	const stash = readTempStash();
	if (!stash) return null;
	try {
		return (decodeJwt(stash.token)?.payload?.sub as string) || null;
	} catch {
		return null;
	}
}

/**
 * Clears any stashed temp-user JWT.
 */
export function clearStashedTempToken(): void {
	localStorage.removeItem(TEMP_STASH_KEY);
}

function tokenSubject(token: string | null | undefined): string | null {
	if (!token) return null;
	try {
		const sub = decodeJwt(token)?.payload?.sub;
		return typeof sub === "string" && sub ? sub : null;
	} catch {
		return null;
	}
}

/**
 * True when `token` belongs to the stashed guest. Compared by identity (JWT
 * `sub`), not the raw string: `/api/auth/me` re-issues the token on every page
 * load, so a live guest session rarely keeps the exact stashed string.
 */
function isStashedGuestToken(stash: TempStash, token: string | null | undefined): boolean {
	if (!token) return false;
	if (token === stash.token) return true;
	const stashSub = tokenSubject(stash.token);
	return stashSub !== null && tokenSubject(token) === stashSub;
}

/**
 * True while a stashed guest session is still awaiting merge: either nothing
 * is logged in, or the live session *is* the stashed guest (e.g. restored by a
 * previous auth attempt that failed, possibly since refreshed by /me). A
 * different live session means a real account, so the stash is not pending.
 * When the live guest token has been refreshed, the stash adopts it.
 */
export function hasPendingTempStash(): boolean {
	const stash = readTempStash();
	if (!stash) return false;
	const liveToken = getAuthToken();
	if (!liveToken) return true;
	if (!isStashedGuestToken(stash, liveToken)) return false;
	if (liveToken !== stash.token) {
		try {
			localStorage.setItem(
				TEMP_STASH_KEY,
				JSON.stringify({ token: liveToken, expiresAt: stash.expiresAt }),
			);
		} catch {
			// Stale stash string is harmless: identity matching still applies.
		}
	}
	return true;
}

/**
 * Query-string suffix for social login/signup URLs that carries the temp
 * session so the backend merges it. A pending stashed temp session (logged-out
 * guest) always requests the strict merge: the permanent token is only issued
 * once the guest's vouchers/entries have moved, and the social-click
 * interceptor restores the temp JWT cookie before navigation if needed.
 */
export function getSocialTempMergeQuery(
	options: { isTempUser?: boolean; requireTempMerge?: boolean } = {},
): string {
	const hasStash = hasPendingTempStash();
	const isTemp = Boolean(options.isTempUser) || hasStash;
	const strict = Boolean(options.requireTempMerge) || hasStash;
	return (
		(isTemp ? "&is_temp=true" : "") + (strict ? "&require_temp_merge=true" : "")
	);
}

/**
 * Restores the stashed temp JWT into the live `token` cookie so the next
 * auth-related backend call carries it (enabling server-side reassignment).
 * The backend only reads the cookie, so this checks the cookie itself rather
 * than `getAuthToken()`: after a failed attempt the restored cookie can expire
 * while the guest token lingers in sessionStorage, and it must be re-set.
 * No-op when no valid stash exists or a different session is live.
 * Returns true if the cookie was (re)written from the stash.
 */
export function restoreStashedTempTokenForAuthAction(): boolean {
	const stash = readTempStash();
	if (!stash) return false;
	// A live cookie is either the guest token already in place or a different
	// (real) session; neither should be overwritten.
	if (getCookie("token")) return false;
	const sessionToken = sessionStorage.getItem("token");
	// Don't clobber a different (real) session.
	if (sessionToken && !isStashedGuestToken(stash, sessionToken)) return false;
	setCookie("token", stash.token, 1, "hours"); // brief lifetime; backend will replace on success
	sessionStorage.setItem("token", stash.token);
	sessionStorage.setItem("is_temporary", "true");
	setCookie("is_temporary", "true", TEMP_USER_COOKIE_EXPIRY_HOURS, "hours");
	return true;
}

/**
 * Installs a one-shot capture-phase click interceptor that restores the
 * stashed temp token before navigating to any `/auth/social/...` URL.
 * Idempotent.
 */
function installSocialStashInterceptor(): void {
	if ((window as any).__tempStashSocialInterceptorInstalled) return;
	(window as any).__tempStashSocialInterceptorInstalled = true;
	document.addEventListener(
		"click",
		(e) => {
			const target = e.target as HTMLElement | null;
			if (!target) return;
			const anchor = target.closest(
				'a[href*="/auth/social/"]',
			) as HTMLAnchorElement | null;
			if (!anchor) return;
			// Restore guards itself: it re-sets the cookie only when it's missing
			// and the remaining session (if any) is the pending guest's.
			restoreStashedTempTokenForAuthAction();
		},
		true,
	);
}

/**
 * Terminates the current temporary user session while preserving the JWT
 * in localStorage so it can be replayed during the user's next signup or
 * login (triggering backend reassignment of their entries/recordings).
 *
 * Safe to call when the user is not a temp user — becomes a normal
 * `clearAuthToken()` in that case.
 */
export function stashTempUserAndClearSession(): void {
	const token = getAuthToken();
	const isTemp = getCookie("is_temporary") === "true";
	if (token && isTemp) {
		try {
			const stash: TempStash = {
				token,
				expiresAt: Date.now() + TEMP_STASH_TTL_MS,
			};
			localStorage.setItem(TEMP_STASH_KEY, JSON.stringify(stash));
			installSocialStashInterceptor();
		} catch (e) {
			console.warn("Failed to stash temp user token:", e);
		}
	}
	clearAuthToken();
}

// On module load, if a stash already exists (e.g., user navigated to login
// page after the stash was created), make sure the social interceptor is
// armed so they can use social signup/login.
if (typeof window !== "undefined" && readTempStash()) {
	installSocialStashInterceptor();
}

/**
 * Clears all per-user auth and cached leaderboard state.
 */
function clearSessionForLogout(): void {
	clearAuthToken();
	clearPendingInnerCircleClaim();
	// A deliberate logout invalidates any pending temp-user link.
	clearStashedTempToken();

	// checkLoginState only overwrites these when the next user has values
	sessionStorage.removeItem("rank");
	sessionStorage.removeItem("total_score");
	sessionStorage.removeItem("lifetime_score");
}

/**
 * Handles the user logout process.
 * Clears auth tokens and redirects to the home page.
 */
export function logout(): void {
	clearSessionForLogout();

	// Redirect to home page
	window.location.href = "/";
}

/**
 * Logs the current user out and reloads the current route. Unauthenticated
 * visitors on the entry page get the login/signup prompt, and completing it
 * reloads the same route as the newly authenticated account.
 */
export function switchAccountAndReload(): void {
	clearSessionForLogout();
	window.location.reload();
}

/**
 * Checks the user's current login state by fetching user data from the API.
 * Updates session storage, PostHog identification, and UI elements (nav credits).
 * Optionally redirects the user if not logged in or if a redirect target is provided.
 *
 * @param shouldRedirect - If true, redirects to login page if not authenticated.
 * @param redirectTarget - Optional URL to redirect to after successful login/state check.
 * @returns A promise resolving to the User object if logged in, or null otherwise.
 */
export async function checkLoginState(
	shouldRedirect: boolean = true,
	redirectTarget: string | null = null,
): Promise<User | null> {
	console.log("Checking login state...");
	// Check for token in query params first (e.g., after social login)
	const tokenFromQuery = new URLSearchParams(window.location.search).get(
		"token",
	);
	if (tokenFromQuery) {
		setAuthToken(tokenFromQuery); // Save token from query param
		// Clean the URL
		const url = new URL(window.location.href);
		url.searchParams.delete("token");
		window.history.replaceState({}, document.title, url.toString());
	}

	console.log("Retrieving auth token...");
	const token = getAuthToken();
	console.log("Auth token found:", !!token);
	if (!token) {
		console.log("No auth token found.");
		if (shouldRedirect) {
			const loginUrl = redirectTarget
				? `/login?redirect=${encodeURIComponent(redirectTarget)}`
				: "/login";
			window.location.href = loginUrl;
			throw new Error("Redirecting to login");
		}
		return null;
	}

	try {
		console.log("Fetching current user data...");
		const user = await fetchCurrentUser(); // API call

		// --- Success ---
		// Update token (API might return a refreshed token)
		//setAuthToken(user.token);
		if (user.leaderboard?.user_rank) {
			sessionStorage.setItem("rank", String(user.leaderboard.user_rank));
		}

		if (user.leaderboard?.total_score) {
			sessionStorage.setItem("total_score", String(user.leaderboard.total_score));
		}

		if (user.leaderboard?.lifetime_score) {
			sessionStorage.setItem(
				"lifetime_score",
				String(user.leaderboard.lifetime_score),
			);
		}

		/* sessionStorage.setItem(
			"inner_circle_prize_pending",
			String(user.leaderboard?.inner_circle_prize_pending)
		); */

		if (user.phone_number) {
			sessionStorage.setItem("phone_number", user.phone_number);
		}

		// Decode token to get payload info
		const decoded = decodeJwt(user.token);
		const payload = decoded.payload as JwtPayload; // Type assertion

		// Store relevant info in session storage
		sessionStorage.setItem("sci", user.sci || ""); // Store Square Customer ID if present
		sessionStorage.setItem("credits", String(user.jackpot_credits || 0));
		sessionStorage.setItem("is_temporary", String(payload.is_temp || false));
		if (user.email) {
			sessionStorage.setItem("user_email", user.email);
		}

		// Update UI (Nav Credits) - only if not temporary
		if (!payload.is_temp) {
			// A permanent session means any stashed guest session has either been
			// merged (e.g. via social login) or is no longer relevant.
			clearStashedTempToken();
			try {
				setNavCredits(user.jackpot_credits || 0);
			} catch (uiError) {
				console.warn("Failed to update nav credits UI:", uiError);
			}
		}

		// Identify user in PostHog
		if (window.posthog?.identify && user.email) {
			try {
				window.posthog.identify(user.email, {
					name: user.full_name, // Add user properties
					// Add other relevant traits
				});
			} catch (phError) {
				console.warn("PostHog identify failed:", phError);
			}
		}

		return user; // Return user data
	} catch (error: any) {
		console.error("Error checking login state:", error);
		// Clear potentially invalid token only if unauthorized error occurs
		if (error.message === "Unauthorized") {
			clearAuthToken();
			// Re-render nav to show logged-out state? Or rely on redirect?
			// renderNav(); // Might cause issues if called before full page load
		}

		if (shouldRedirect) {
			// Redirect to login, preserving original target if specified
			const loginUrl = redirectTarget
				? `/login?redirect=${encodeURIComponent(redirectTarget)}`
				: "/login";
			window.location.href = loginUrl;
			return null;
		}
		return null; // Indicate user is not authenticated
	}
}

/**
 * Handles the submission logic for the login form.
 * Calls the login API, sets the auth token, and redirects.
 * @param username - User's email/username.
 * @param password - User's password.
 * @param redirectTarget - Optional URL to redirect to on success. Defaults to '/dashboard'.
 */
type LoginSubmitOptions = {
	skipRedirect?: boolean;
	claimInnerCircle?: boolean;
	requireTempMerge?: boolean;
};

type LoginApiOptions = Pick<
	LoginSubmitOptions,
	"claimInnerCircle" | "requireTempMerge"
>;

async function completeLogin(
	request: (options: LoginApiOptions) => Promise<LoginResponse>,
	redirectTarget: string,
	options: LoginSubmitOptions | undefined,
	showErrorToast: boolean,
): Promise<void> {
	// If the user previously had a temp session that we stashed, restore it as
	// the `token` cookie so the backend's login handler can merge their temp
	// entries/recordings into the account they're logging in to.
	const restoredTempStash = restoreStashedTempTokenForAuthAction();
	// Also true on a retry, when an earlier failed attempt left the stashed
	// guest token live (restore then returns false).
	const pendingTempStash = restoredTempStash || hasPendingTempStash();
	try {
		const loginData = await request({
			claimInnerCircle: options?.claimInnerCircle,
			// A replayed stash is a guest session: strict merge so its vouchers
			// can never be stranded on the temp account.
			requireTempMerge: options?.requireTempMerge || pendingTempStash,
		});

		if (pendingTempStash) {
			clearStashedTempToken();
		}

		// Set token and user info
		//setAuthToken(loginData.access_token); // Use access_token from login response
		sessionStorage.setItem("sci", loginData.sci || "");
		sessionStorage.setItem("credits", String(loginData.jackpot_credits || 0));

		// Decode token to get temp status etc.
		const decoded = decodeJwt(loginData.access_token);
		persistAuthToken(loginData.access_token, {
			isTemp: Boolean(decoded.payload.is_temp),
		});
		sessionStorage.setItem(
			"is_temporary",
			String(decoded.payload.is_temp || false),
		);

		// Identify user in PostHog
		if (window.posthog?.identify) {
			try {
				window.posthog.identify(
					decoded.payload.sub, // Use 'sub' from JWT as user ID
					{ email: decoded.payload.sub }, // Add email trait if sub is email
				);
			} catch (phError) {
				console.warn("PostHog identify failed during login:", phError);
			}
		}

		if (!options?.skipRedirect) {
			window.location.href = redirectTarget;
		}
	} catch (error) {
		console.error("Login failed:", error);
		if (showErrorToast) {
			messageToast({
				message:
					error instanceof Error
						? error.message
						: "Login failed. Please try again.",
				background: "text-bg-danger",
			});
		}
		throw error; // Re-throw to allow form to handle UI updates (e.g., re-enable button)
	}
}

export async function handleLoginSubmit(
	username: string,
	password: string,
	redirectTarget: string = "/dashboard",
	options?: LoginSubmitOptions,
): Promise<void> {
	return completeLogin(
		(apiOptions) => loginUser(username, password, apiOptions),
		redirectTarget,
		options,
		true,
	);
}

export async function handleLoginCodeSubmit(
	email: string,
	code: string,
	redirectTarget: string = "/dashboard",
	options?: LoginSubmitOptions,
): Promise<void> {
	return completeLogin(
		(apiOptions) => loginWithCode(email, code, apiOptions),
		redirectTarget,
		options,
		false,
	);
}

/**
 * Handles the submission logic for the signup form.
 * Calls the create account API, sets the auth token, and redirects.
 * @param name - User's full name.
 * @param email - User's email.
 * @param password - User's chosen password.
 * @param redirectTarget - Optional URL to redirect to on success. Defaults to '/dashboard'.
 */
export async function handleSignupSubmit(
	name: string,
	email: string,
	password: string,
	redirectTarget: string = "/dashboard",
): Promise<void> {
	// Replay any stashed temp session so signup carries the temp identifier
	// and the backend reassigns the prior recordings to the new account.
	const restoredTempStash = restoreStashedTempTokenForAuthAction();
	const pendingTempStash = restoredTempStash || hasPendingTempStash();
	const isTemporary = getCookie("is_temporary") === "true";
	const temporaryEmail =
		isTemporary && getAuthToken()
			? decodeJwt(getAuthToken())?.payload?.sub
			: null;

	try {
		const accountData = await createUserAccount({
			email: email,
			full_name: name,
			password: password,
			is_temp: isTemporary, // Pass current temp status
			temporary_email: temporaryEmail, // Pass temp email if upgrading
		});

		if (pendingTempStash) {
			clearStashedTempToken();
		}

		// Set token and user info
		//setAuthToken(accountData.access_token);
		// Clear temp status after successful upgrade/creation
		sessionStorage.removeItem("is_temporary");
		// Other session items like credits/sci might not be available immediately after signup

		// Identify user in PostHog
		if (window.posthog) {
			try {
				window.posthog.identify(email, { name: name, email: email });
				window.posthog.capture("account_created", { method: "email" }); // Track signup event
			} catch (phError) {
				console.warn("PostHog identify/capture failed during signup:", phError);
			}
		}

		// Redirect after successful signup
		window.location.href = redirectTarget;
	} catch (error: any) {
		console.error("Signup failed:", error);
		if (!isUserExistsError(error)) {
			messageToast({
				message: error.message || "Signup failed. Please try again.",
				background: "text-bg-danger",
			});
		}
		throw error; // Re-throw to allow form to handle UI updates
	}
}

export function isUserExistsError(error: unknown): boolean {
	const message =
		typeof error === "object" && error && "message" in error
			? String((error as { message?: string }).message || "")
			: "";
	return /user already exists|already exists/i.test(message);
}

/**
 * Handles submission of the password reset request form.
 * @param email The user's email address.
 */
export async function handlePasswordResetRequestSubmit(
	email: string,
): Promise<void> {
	try {
		try {
			window.posthog?.capture?.("password_reset_request_submitted", {
				flow: "password_reset",
				email,
			});
		} catch {
			/* noop */
		}
		await requestPasswordReset(email);
		try {
			window.posthog?.capture?.("password_reset_request_success", {
				flow: "password_reset",
				email,
			});
		} catch {
			/* noop */
		}
		messageToast({
			message:
				"Password reset has been requested. Please check your email for instructions. If you haven't received an email, please check your spam folder.",
			background: "text-bg-success",
			delay: 10000, // Show for 10 seconds
		});
		// Optionally, disable the form or show a success message in place of the form
		const form = document.getElementById("passwordResetForm");
		if (form) {
			form.innerHTML =
				'<p class="text-center text-white">An email with a unique link to reset your password has been sent. Please check your email.</p>';
		}
	} catch (error: any) {
		try {
			window.posthog?.capture?.("password_reset_request_failed", {
				flow: "password_reset",
				email,
				error_message: error?.message || "unknown",
			});
		} catch {
			/* noop */
		}
		console.error("Password reset request failed:", error);
		messageToast({
			message: error.message || "Something went wrong. Please try again.",
			background: "text-bg-danger",
		});
		throw error; // Re-throw to allow form to re-enable button
	}
}

/**
 * Handles submission of the new password form.
 * @param token The password reset token.
 * @param password The new password.
 * @param passwordConfirm The password confirmation.
 */
export async function handleNewPasswordSubmit(
	token: string,
	password: string,
	passwordConfirm: string,
): Promise<void> {
	try {
		try {
			window.posthog?.capture?.("password_reset_submitted", {
				flow: "password_reset",
			});
		} catch {
			/* noop */
		}
		await submitNewPassword(token, password, passwordConfirm);
		try {
			window.posthog?.capture?.("password_reset_success", {
				flow: "password_reset",
			});
		} catch {
			/* noop */
		}
		messageToast({
			message:
				"Password reset successful. You can now log in with your new password.",
			background: "text-bg-success",
		});
		// Redirect to login page after a short delay
		setTimeout(() => {
			window.location.href = "/login";
		}, 3000);
	} catch (error: any) {
		try {
			window.posthog?.capture?.("password_reset_failed", {
				flow: "password_reset",
				error_message: error?.message || "unknown",
			});
		} catch {
			/* noop */
		}
		console.error("New password submission failed:", error);
		messageToast({
			message: error.message || "Something went wrong. Please try again.",
			background: "text-bg-danger",
		});
		throw error; // Re-throw to allow form to re-enable button
	}
}
